import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ackEvent, getEventDetail, listEvents } from '../../api/eventsApi';
import { onReconnected, subscribe, useWebSocket } from '../../hooks/useWebSocket';
import type { EventAckInfo, EventDetail, EventListItem } from '../../api/types';
import { filtersToQuery, type Severity, type SystemLogFilters } from './systemLog.types';

// 권위: docs/ARCH_DASHBOARD.md §4.8 — 화면 8 실시간 + 목록 + 상세 + ack 훅 모음.
// - WebSocket `/topic/system-events` 구독, 현재 필터 조건을 통과한 이벤트만 최상단 prepend.
// - 실시간으로 추가된 이벤트는 최대 50건까지 보존하고 넘치면 오래된 것부터 drop.
// - WebSocket 단절 시에는 30초 폴링 폴백 (§4.8 "폴링 모드").

const QK_LIST = 'system-events-list';
const QK_DETAIL = 'system-event-detail';

const REALTIME_BUFFER_MAX = 50;

interface RealtimeResult {
  data: { items: EventListItem[]; total: number; unackedCount: number } | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  realtimeCount: number; // 서버 응답에 아직 합쳐지지 않은 WS prepend 건수.
  clearRealtime: () => void;
}

export function useSystemLogList(filters: SystemLogFilters): RealtimeResult {
  const { status: wsStatus } = useWebSocket();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QK_LIST, filters],
    queryFn: () => listEvents(filtersToQuery(filters)),
    // WS 연결 시에는 실시간만 신뢰. 끊기면 30초 폴링 (§4.8).
    refetchInterval: wsStatus === 'connected' ? false : 30_000,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  // ── 실시간 버퍼 — 현재 필터와 정확히 일치하는 신규 이벤트만 보존.
  const [realtime, setRealtime] = useState<EventListItem[]>([]);
  // 필터가 바뀌면 버퍼를 비우고 서버 응답과의 혼동을 방지.
  useEffect(() => {
    setRealtime([]);
  }, [
    filters.severities.join(','),
    filters.sources.join(','),
    filters.eventCode,
    filters.from,
    filters.to,
    filters.q,
    filters.unackedOnly,
    filters.page,
    filters.pageSize,
  ]);

  // 재연결 성공 시 최신 데이터 재조회.
  useEffect(() => {
    const off = onReconnected(() => {
      queryClient.invalidateQueries({ queryKey: [QK_LIST] });
    });
    return off;
  }, [queryClient]);

  // WebSocket 구독 — `/topic/system-events` 에 들어오는 SystemEvent payload 를 EventListItem 으로 정규화.
  useEffect(() => {
    const off = subscribe('/topic/system-events', (msg) => {
      if (!msg.body) return;
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(msg.body);
      } catch {
        return;
      }
      const item = normalizeIncoming(parsed);
      if (!item) return;
      // 페이징이 0페이지가 아니면 prepend 하지 않는다 — 오래된 페이지를 보는 중이므로 최신 삽입은 의미 없음.
      if (filters.page !== 0) return;
      if (!matchesFilters(item, filters)) return;

      setRealtime((prev) => {
        // 같은 eventId 중복 방지.
        if (prev.some((p) => p.eventId === item.eventId)) return prev;
        const next = [item, ...prev];
        if (next.length > REALTIME_BUFFER_MAX) next.length = REALTIME_BUFFER_MAX;
        return next;
      });
    });
    return off;
  }, [
    // 필터 의존 — 핸들러 안에서 최신 필터로 매칭할 수 있도록 재구독.
    filters.severities.join(','),
    filters.sources.join(','),
    filters.eventCode,
    filters.from,
    filters.to,
    filters.q,
    filters.unackedOnly,
    filters.page,
    filters.pageSize,
  ]);

  // 합치기 — 서버 아이템 중 realtime 에 이미 있는 eventId 는 제거 (중복 방지).
  const serverItems = query.data?.items ?? [];
  const mergedItems = (() => {
    if (realtime.length === 0) return serverItems;
    const seen = new Set(realtime.map((r) => r.eventId));
    const dedupedServer = serverItems.filter((it) => !seen.has(it.eventId));
    const merged = [...realtime, ...dedupedServer];
    // pageSize 상한을 넘지 않도록 자름 — 서버 pageSize 와 realtime 합계를 UI 에서는 pageSize 로 제한.
    if (merged.length > filters.pageSize) merged.length = filters.pageSize;
    return merged;
  })();

  return {
    data: query.data
      ? {
          items: mergedItems,
          total: query.data.total,
          unackedCount: query.data.unackedCount,
        }
      : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    isFetching: query.isFetching,
    realtimeCount: realtime.length,
    clearRealtime: () => setRealtime([]),
  };
}

export function useSystemLogDetail(eventId: string | null) {
  return useQuery({
    queryKey: [QK_DETAIL, eventId],
    queryFn: () => getEventDetail(eventId as string),
    enabled: eventId != null,
    staleTime: 5_000,
  });
}

export function useAckEventMutation(ackBy: string) {
  const qc = useQueryClient();
  const lastErrorRef = useRef<Error | null>(null);

  // 상세 캐시는 두 곳에 존재한다:
  //   - ['system-event-detail', eventId]     (화면 8 SystemLog)
  //   - ['agent-history-detail', eventId]    (화면 4 AgentHistory)
  // 옵티미스틱으로 acks 배열에 1건 추가하면 상세 모달의 "이미 읽음 처리됨" 분기가
  // mutation 성공/서버 refetch 를 기다리지 않고 즉시 반영된다.
  const DETAIL_KEYS = ['system-event-detail', 'agent-history-detail'] as const;

  const mut = useMutation({
    mutationFn: (args: { eventId: string; note?: string }) =>
      ackEvent(args.eventId, ackBy, args.note),
    onMutate: async (args: { eventId: string; note?: string }) => {
      // 진행 중인 detail refetch 를 취소해 옵티미스틱 업데이트가 덮이지 않게 한다.
      const snapshots: Array<{ key: readonly unknown[]; prev: EventDetail | undefined }> = [];
      const optimisticAck: EventAckInfo = {
        ackBy,
        ackAt: new Date().toISOString(),
        note: args.note ?? null,
      };

      for (const base of DETAIL_KEYS) {
        const key = [base, args.eventId] as const;
        await qc.cancelQueries({ queryKey: key });
        const prev = qc.getQueryData<EventDetail | undefined>(key as unknown as readonly unknown[]);
        if (prev) {
          qc.setQueryData<EventDetail>(key as unknown as readonly unknown[], {
            ...prev,
            acks: [...(prev.acks ?? []), optimisticAck],
          });
        }
        snapshots.push({ key: key as unknown as readonly unknown[], prev });
      }

      return { snapshots };
    },
    onError: (e: Error, _args, ctx) => {
      lastErrorRef.current = e;
      // 롤백
      if (ctx?.snapshots) {
        for (const { key, prev } of ctx.snapshots) {
          if (prev) qc.setQueryData<EventDetail>(key, prev);
          else qc.removeQueries({ queryKey: key, exact: true });
        }
      }
    },
    onSuccess: () => {
      lastErrorRef.current = null;
    },
    onSettled: () => {
      // 목록·알림센터는 서버 값 기준으로 재조회. 상세는 옵티미스틱 업데이트가
      // 이미 적용되었으므로 여기서 invalidate 하지 않는다 — 즉시 refetch 하면
      // 서버가 새 ack 를 반영하기 전 이전 상태를 되돌려 UI 가 깜빡일 수 있다.
      // 모달을 닫고 다시 열면 staleTime 만료 후 자연스럽게 재조회됨.
      qc.invalidateQueries({ queryKey: [QK_LIST] });
      qc.invalidateQueries({ queryKey: ['agent-history-list'] });
      qc.invalidateQueries({ queryKey: ['events', 'unacked-critical'] });
    },
  });
  return mut;
}

// ── 내부 헬퍼 ─────────────────────────────────────────────

/**
 * WebSocket 로 들어오는 SystemEvent payload → EventListItem.
 * - camelCase / snake_case 모두 수용 (백엔드 직렬화 정책 차이 대응, NotificationCenter 와 동일 방식).
 * - 필수 필드가 없으면 null 반환(=무시).
 */
function normalizeIncoming(p: Record<string, unknown>): EventListItem | null {
  const eventId =
    (p.eventId as string | undefined) ??
    (p.event_id as string | undefined) ??
    (p.id as string | undefined);
  const severity = ((p.severity as string | undefined) ?? '').toUpperCase();
  const occurredAt =
    (p.occurredAt as string | undefined) ??
    (p.occurred_at as string | undefined) ??
    new Date().toISOString();
  const eventCode =
    (p.eventCode as string | undefined) ?? (p.event_code as string | undefined) ?? '';
  const sourceAgent =
    (p.sourceAgent as string | undefined) ?? (p.source_agent as string | undefined) ?? '';
  const summary = (p.summary as string | undefined) ?? '';
  const traceId =
    (p.traceId as string | undefined) ?? (p.trace_id as string | undefined) ?? null;
  const entityRefs =
    (p.entityRefs as Record<string, unknown> | undefined) ??
    (p.entity_refs as Record<string, unknown> | undefined) ??
    null;

  if (!eventId || !severity) return null;
  if (!['FATAL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].includes(severity)) return null;

  return {
    eventId,
    occurredAt,
    severity: severity as EventListItem['severity'],
    sourceAgent,
    eventCode,
    summary,
    entityRefs,
    traceId,
    acked: false,
  };
}

/** 현재 필터 바에 걸린 조건을 클라이언트에서 재확인 — WS 로 온 이벤트의 prepend 여부 판정. */
function matchesFilters(it: EventListItem, f: SystemLogFilters): boolean {
  if (f.severities.length > 0 && !f.severities.includes(it.severity as Severity)) return false;
  if (f.sources.length > 0 && !f.sources.includes(it.sourceAgent)) return false;
  if (f.eventCode.trim()) {
    if (!matchesGlob(it.eventCode, f.eventCode.trim())) return false;
  }
  if (f.q.trim()) {
    const needle = f.q.trim().toLowerCase();
    if (!it.summary.toLowerCase().includes(needle)) return false;
  }
  if (f.unackedOnly && it.acked) return false;
  if (f.from) {
    if (new Date(it.occurredAt) < new Date(f.from)) return false;
  }
  if (f.to) {
    if (new Date(it.occurredAt) >= new Date(f.to)) return false;
  }
  return true;
}

/** `LEADING.*` 스타일 glob → 정규식. 지원 메타문자: `*` (그 외는 literal). */
function matchesGlob(code: string, pattern: string): boolean {
  if (!pattern.includes('*')) return code === pattern;
  const re = new RegExp(
    '^' +
      pattern
        .split('*')
        .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('.*') +
      '$'
  );
  return re.test(code);
}
